import React, { useRef, useState } from 'react'
import moment from 'moment'
import { Box, Modal } from '@mui/material'
import { supportType } from '../services/types/APIResponseType'
import useAdminReplyToCloseTicket from '@/services/hooks/useAdminReply'
import { ApiStatusConstants } from '@/utils/config/auth-enums'

type Props = {
  open: boolean
  userDetails: supportType | null
  onClose: () => void
  handleConfirmation: () => void
  updateListData: (val: boolean) => void
}

const ModelContainerStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  color: 'white',
}

const MobileContactDetailsModel: React.FC<Props> = ({
  open,
  userDetails,
  onClose,
  handleConfirmation,
  updateListData,
}) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const [inputValue, setInputValue] = React.useState('')
  const [adminResponse, setAdminResponse] = React.useState(
    userDetails?.responseText ?? '',
  )
  const [, setErrorMsg] = useState<string>('')
  const [, setApiStatus] = useState<ApiStatusConstants>(
    ApiStatusConstants.INITIAL,
  )

  const { mutate: adminReplay } = useAdminReplyToCloseTicket()

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      submitMessage()
    }
  }

  const submitMessage = () => {
    setApiStatus(ApiStatusConstants.LOADING)
    setAdminResponse(inputValue)
    adminReplay(
      {
        payload: {
          ticketId: userDetails?._id,
          responseText: inputValue,
        },
      },
      {
        onSuccess: () => {
          setApiStatus(ApiStatusConstants.SUCCESS)
          updateListData(true)
        },
        onError: (error) => {
          setApiStatus(ApiStatusConstants.FAILURE)
          setErrorMsg(
            error.message || 'Something went wrong. Please try again.',
          )
        },
      },
    )
    updateListData(false)
    setInputValue('')
    if (inputRef.current) {
      inputRef.current.style.height = '56px'
    }
  }

  return (
    <React.Fragment>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <Box
          sx={{
            ...ModelContainerStyle,
            width: { xs: 'calc(100% - 24px)', sm: '90%', md: 830 },
            maxWidth: 830,
          }}
          className="bg-grad before:rounded-[40px] bg-[#2B24600D] padding-none overflow-hidden"
        >
          <div className="px-5 py-6 md:px-[80px] md:py-[52px] rounded-[40px] backdrop-blur-xl">
            <div className="relative">
              <img
                onClick={() => onClose()}
                className="cursor-pointer absolute right-0 -top-2 md:-right-14 md:-top-5"
                src="./close_icon.svg"
                alt="close icon"
              />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl mb-6 md:mb-10">
                Contact Form Details
              </h3>
            </div>
            <div>
              <div
                className={`grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-10 font-normal overflow-auto  ${!userDetails?.markAsClosed ? 'h-[50vh] md:h-[40vh]' : 'h-[65vh] md:h-[60vh]'}`}
              >
                <div className="col-span-2 md:border-r md:border-[#333]">
                  <p className="text-sm text-[#7D7D7D]  mb-2">Name</p>
                  <p className="text-base md:text-xl">
                    {[
                      userDetails?.profileId?.firstName,
                      userDetails?.profileId?.lastName,
                    ]
                      .filter(Boolean)
                      .join(' ') || 'N/A'}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-[#7D7D7D]  mb-2">Email Address</p>
                  <p className="text-base md:text-xl">
                    {userDetails?.userId?.email || 'N/A'}
                  </p>
                </div>
                <div className="col-span-2 md:border-r md:border-[#333]">
                  <p className="text-sm text-[#7D7D7D]  mb-2">Phone Number</p>
                  <p className="text-base md:text-xl">
                    {userDetails?.profileId?.phone
                      ? [
                          userDetails?.profileId?.countryCode,
                          userDetails?.profileId?.phone,
                        ]
                          .filter(Boolean)
                          .join(' ')
                      : 'N/A'}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-[#7D7D7D]  mb-2">Date & Time</p>
                  <p className="text-base md:text-xl">
                    {userDetails?.updatedAt
                      ? moment(userDetails?.updatedAt).format(
                          'YYYY/MM/DD | h:mm',
                        )
                      : 'N/A'}
                  </p>
                </div>
                <div className="col-span-4">
                  <p className="text-sm text-[#7D7D7D] mb-2">Description</p>
                  <p className="text-base md:text-xl break-all w-full md:w-[500px]">
                    {userDetails?.description || 'N/A'}
                  </p>
                </div>
                {userDetails?.supportQueryImage && (
                  <div className="col-span-4">
                    <p className="text-sm text-[#7D7D7D] mb-4">Attachments</p>
                    <div className="flex gap-2">
                      <img
                        className="w-[100px] h-[100px] object-cover"
                        src={userDetails?.supportQueryImage}
                        alt="query"
                        draggable={false}
                      />
                    </div>
                  </div>
                )}
                {adminResponse !== '' && (
                  <div className="col-span-4">
                    <p className="text-sm text-[#7D7D7D]  mb-2">
                      Admin Response
                    </p>
                    <p className="text-base md:text-xl break-all w-full md:w-[500px]">
                      {adminResponse}
                    </p>
                  </div>
                )}
              </div>
              {!userDetails?.markAsClosed && (
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center justify-center mt-8 md:mt-12">
                  <div className="md:col-span-4">
                    {adminResponse === '' && (
                      <div className="w-full bg-grad p-[1px] before:rounded-[25px] relative bottom-0 overflow-hidden">
                        <div className="relative rounded-[25px] backdrop-blur-lg bg-[#1D1D1D] flex items-start">
                          <textarea
                            ref={inputRef}
                            rows={1}
                            placeholder="Type message here ....."
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            onInput={(
                              e: React.FormEvent<HTMLTextAreaElement>,
                            ) => {
                              const target =
                                e.currentTarget as HTMLTextAreaElement
                              target.style.height = 'auto'
                              target.style.height = `${target.scrollHeight}px`
                            }}
                            className="!h-auto !resize-none self-center bg-[#0000] text-xl flex-grow border-none outline-none rounded-lg p-4"
                          />
                          <button
                            onClick={() => submitMessage()}
                            className="text-white rounded-full px-4 py-4 my-2 mr-2 bg-[#FB5012]"
                          >
                            <img src="./send-icon.svg" alt="send" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <button
                      onClick={() => handleConfirmation()}
                      className="flex items-center justify-center text-base md:text-xl w-full bg-[#FB5012] py-3 md:py-5 px-4 border-none outline-none rounded-3xl"
                    >
                      Mark as Closed
                      <img
                        className="ml-2 w-6"
                        src="./check_icon.svg"
                        alt="check"
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Box>
      </Modal>
    </React.Fragment>
  )
}

export default MobileContactDetailsModel
